/**
 * World 3 — "Duskfall Cascade" (145 BPM, mint/electric-blue). Teach then
 * combine the R2 parts (brief §4.1): l1 throttle, l2 floaty arcs + ramps,
 * l3 tunnels 2.0 + ice, l4 heavy finale.
 */
import { buildLevel, every, rep, F, GAP } from "./build";
import type { LevelFile } from "../track/types";

const G3 = [GAP, GAP, GAP];
const G4 = [GAP, GAP, GAP, GAP];

/** ~69 s, normal, minThrottle 1.3 — the throttle playground: long runways
 *  into 3-row leaps, brake chicanes right after. */
export const W3_L1: LevelFile = buildLevel({
  id: "w3-l1",
  name: "Duskfall Approach",
  songId: "duskfall",
  world: 3,
  physics: { gravity: "normal" },
  minThrottle: 1.3,
  rows: [
    ...rep(F, 14),
    ...every(4, "___1___", "__1____", "____1__", "_1___1_"),
    ...rep(F, 8), // runway — build to 1.3+
    "BBBBBBB",
    ...rep(F, 3), // pad close: the boost window covers the whole arc
    ...G3,
    ...rep(F, 8),
    "_o_____",
    ...rep(F, 5),
    // brake chicane: dense weave punishes arriving hot
    ...every(4, "_11_11_", "__1_1__", "_1___1_", "11___11"),
    ...rep(F, 8),
    "BBBBBBB",
    ...rep(F, 3),
    ...G3,
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 4),
    ...rep(F, 6),
    "BBBBBBB",
    ...rep(F, 3), // boosted runway
    ...G3,
    ...rep(F, 8),
    "___o___",
    ...rep(F, 5),
    ...every(4, "11_1_11", "_11_11_", "1__1__1"),
    ...rep(F, 8),
    "BBBBBBB",
    ...rep(F, 3),
    ...G3,
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 4),
    // narrow corridor at 1.3+ throttle — no obstacles, pure nerve
    ...rep("._____.", 2),
    ...rep("..___..", 4),
    ...rep("._____.", 2),
    ...rep(F, 8),
    "BBBBBBB",
    ...rep(F, 3),
    ...G3,
    ...rep(F, 6),
    "_____o_",
    ...rep(F, 5),
    ...every(4, "__2____", "____2__"),
    ...rep(F, 8),
    ...every(4, "_1___1_", "__1_1__", "1__1__1"),
    ...rep(F, 8), // final runway
    "BBBBBBB",
    ...rep(F, 3),
    GAP, GAP, GAP,
    ...rep(F, 8),
    ...every(4, "_11_11_", "11___11"),
    ...rep(F, 18),
    "FFFFFFF",
  ],
});

/** ~76 s, floaty — moon arcs: 4-row gaps, ramp ridges and launch lips,
 *  height-2 hops (floaty apex 2.6 clears them). */
export const W3_L2: LevelFile = buildLevel({
  id: "w3-l2",
  name: "Moonrise",
  songId: "duskfall",
  world: 3,
  physics: { gravity: "floaty" },
  rows: [
    ...rep(F, 14),
    ...every(4, "__1____", "____1__", "_1___1_"),
    ...rep(F, 6),
    ...G4, // first moon arc
    ...rep(F, 8),
    "_o_____",
    ...rep(F, 5),
    ...rep(F, 2),
    "///////",
    "1111111",
    "1111111",
    "\\\\\\\\\\\\\\",
    ...rep(F, 6),
    ...G4,
    ...rep(F, 6),
    ...G4, // arc chain
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 4),
    ...every(4, "__2_2__", "_2___2_"),
    ...rep(F, 4),
    "///////",
    ...rep(F, 8), // launch lip — floaty flight
    "___o___",
    ...rep(F, 5),
    ...G4,
    ...rep(F, 4),
    GAP,
    GAP,
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 4),
    // moon-arc island hops: floaty reach makes Δ2 lanes over 2 void rows a
    // long, glorious diagonal flight
    "..___..",
    "..__...",
    ...rep(".._....", 3),
    GAP,
    GAP,
    ...rep("...._..", 3),
    "..___..",
    F,
    ...rep(F, 6),
    ...G4,
    ...rep(F, 8),
    "______o",
    ...rep(F, 5),
    ...every(4, "_11_11_", "__1_1__"),
    ...rep(F, 6),
    "///////",
    ...rep(F, 8),
    GAP, GAP, GAP, GAP,
    ...rep(F, 6),
    ...every(4, "__1____", "____1__", "_1___1_"),
    ...rep(F, 6),
    GAP, GAP, GAP, GAP,
    ...rep(F, 8),
    ...every(4, "__2_2__", "_2___2_"),
    ...rep(F, 4),
    "///////",
    "1111111",
    "\\\\\\\\\\\\\\",
    ...rep(F, 6),
    GAP, GAP, GAP, GAP,
    ...rep(F, 6),
    ...every(4, "_1___1_", "__1_1__"),
    ...rep(F, 16),
    "FFFFFFF",
  ],
});

/** ~79 s, normal — Tunnels 2.0 showcase: U holes, ice approaches sliding
 *  into capped-hop sections, narrow tunnels. */
export const W3_L3: LevelFile = buildLevel({
  id: "w3-l3",
  name: "Understreet",
  songId: "duskfall",
  world: 3,
  physics: { gravity: "normal" },
  rows: [
    ...rep(F, 14),
    ...every(4, "____1__", "__1____", "_1___1_"),
    ...rep(F, 4),
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 2),
    ...rep(F, 6),
    "_o_____",
    ...rep(F, 5),
    ...rep("IIIIIII", 4), // ice approach…
    ...rep("TTTTTTT", 3), // …sliding straight into the tunnel
    "UUUUUUU",
    ...rep("TTTTTTT", 2),
    ...rep(F, 6),
    "CCCCCCC",
    ...rep(F, 4),
    ...every(4, "XX___XX", "_X_X_X_", "X__X__X"),
    GAP,
    ...rep(F, 3),
    "___o___",
    ...rep(F, 5),
    ...rep("..TTT..", 6), // narrow tunnel
    "..___..",
    "..___..",
    ...rep(F, 4),
    "___B___",
    ...rep(F, 9),
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 2),
    ...rep(F, 6),
    "CCCCCCC",
    ...rep(F, 4),
    ...rep("IIIIIII", 3),
    "I1III1I",
    ...rep("IIIIIII", 2),
    ...rep(F, 4),
    GAP,
    ...rep(F, 3),
    "______o",
    ...rep(F, 5),
    // single-lane ICE bridge: the funnel is frozen — commit to center early
    ...rep("..III..", 2),
    ...rep("...I...", 4),
    ...rep("..III..", 2),
    ...rep(F, 6),
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 2),
    ...rep(F, 6),
    ...every(4, "XX___XX", "X__X__X", "_X_X_X_"),
    ...rep(F, 6),
    GAP,
    ...rep(F, 3),
    ...rep("..TTT..", 4),
    "..___..",
    "..___..",
    ...rep(F, 6),
    ...rep("IIIIIII", 4),
    ...rep(F, 4),
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 3),
    "UUUUUUU",
    ...rep("TTTTTTT", 2),
    ...rep(F, 6),
    ...every(4, "_11_11_", "__1_1__", "1__1__1"),
    ...rep(F, 6),
    GAP,
    ...rep(F, 3),
    ...every(4, "X__X__X", "_X_X_X_"),
    ...rep(F, 16),
    "FFFFFFF",
  ],
});

/** ~83 s, heavy, minThrottle 1.4 — the finale: drum-tight hops, tunnel-ice,
 *  and ONE full-throttle leap as the climax. */
export const W3_L4: LevelFile = buildLevel({
  id: "w3-l4",
  name: "Duskfall Terminus",
  songId: "duskfall",
  world: 3,
  physics: { gravity: "heavy" },
  minThrottle: 1.4,
  rows: [
    ...rep(F, 14),
    ...every(4, "__1____", "____1__", "_1___1_"),
    // drum hops: 1-row gaps in tight rhythm
    ...rep(F, 4),
    GAP, F, F,
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 6),
    "_o_____",
    ...rep(F, 5),
    ...every(4, "_11_11_", "__2_2__"),
    ...rep(F, 4),
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 6),
    "CCCCCCC",
    ...rep(F, 4),
    // tunnel run (heavy hops fly clean under the roof — apex 1.0)
    ...rep("TTTTTTT", 4),
    ...rep(F, 4),
    ...rep("IIIIIII", 4),
    "I1III1I",
    ...rep("IIIIIII", 2),
    ...rep(F, 4),
    "___o___",
    ...rep(F, 5),
    GAP, F, F,
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 6),
    "CCCCCCC",
    ...rep(F, 4),
    // heavy snake: the single lane itself drifts sideways — steer, don't jump
    "..___..",
    "..__...",
    ...rep(".._....", 2),
    "..__...",
    ...rep("..._...", 2),
    "...__..",
    ...rep("...._..", 2),
    "...__..",
    "..___..",
    ...rep(F, 6),
    GAP, F, F,
    GAP,
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 4),
    ...every(4, "__1_1__", "_1___1_"),
    ...rep(F, 4),
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 8),
    ...rep("TTTTTTT", 3),
    ...rep(F, 6),
    // THE CLIMAX (the 1993 move): full throttle + boost into a RAMP-CREST
    // jump over a double gap — heavy jumps are 1 row·eff on the flat, but
    // launching from the crest's height clears 2 rows with real margin
    ...rep(F, 8), // runway to reach 1.4 first
    "BBBBBBB",
    ...rep(F, 2),
    "///////",
    GAP,
    GAP,
    ...rep(F, 8),
    "______o",
    ...rep(F, 5),
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 6),
    GAP, F, F,
    GAP, F, F,
    GAP, F, F,
    GAP,
    ...rep(F, 8),
    GAP, F, F,
    GAP,
    ...rep(F, 20),
    "FFFFFFF",
  ],
});
